<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import {
  deleteAdminPaymentProduct,
  listAdminPaymentProducts,
  saveAdminPaymentProduct,
  syncAdminPaymentProductsToStripe,
  type AdminPaymentProduct
} from "../admin-api";

type ProductForm = Omit<AdminPaymentProduct, "createdAt" | "updatedAt">;

const products = ref<AdminPaymentProduct[]>([]);
const loading = ref(false);
const saving = ref(false);
const syncingStripe = ref(false);
const deletingProductId = ref("");
const detailProduct = ref<AdminPaymentProduct | null>(null);
const editorMode = ref<"create" | "edit" | null>(null);
const form = reactive<ProductForm>(blankProduct());

const columns = [
  { title: "套餐", key: "title" },
  { title: "类型", key: "productType", width: 110 },
  { title: "地区", key: "region", width: 110 },
  { title: "额度", key: "credits", width: 110 },
  { title: "宠物位", key: "petSlots", width: 100 },
  { title: "价格", key: "amount", width: 150 },
  { title: "状态", key: "status", width: 100 },
  { title: "排序", key: "sortOrder", dataIndex: "sortOrder", width: 90 },
  { title: "操作", key: "actions", width: 230 }
];

const activeCount = computed(() => products.value.filter((item) => item.status === "active").length);
const totalCredits = computed(() => products.value.reduce((sum, item) => sum + item.credits, 0));
const lowestPrice = computed(() => products.value.length ? Math.min(...products.value.map((item) => item.amountCny)) : 0);
const globalActiveCount = computed(() => products.value.filter((item) => item.region === "GLOBAL" && item.status === "active").length);
const isCreditsProduct = computed(() => form.productType === "credits");
const isPetSlotProduct = computed(() => form.productType === "pet_slot");

onMounted(loadProducts);

watch(
  () => form.productType,
  (productType) => {
    if (productType === "credits") {
      form.petSlots = 0;
      form.credits = Math.max(1, form.credits || 10);
    } else {
      form.credits = 0;
      form.petSlots = Math.max(1, form.petSlots || 1);
    }
  }
);

watch(
  () => form.region,
  (region) => {
    const normalizedRegion = String(region || "").toUpperCase();
    form.region = normalizedRegion;
    if (normalizedRegion === "GLOBAL") {
      form.currency = "AUD";
    } else if (normalizedRegion === "CN") {
      form.currency = "CNY";
    }
  }
);

function blankProduct(): ProductForm {
  return {
    productId: "",
    productType: "credits",
    title: "",
    description: "",
    region: "CN",
    credits: 10,
    petSlots: 0,
    amountCny: 29.9,
    currency: "CNY",
    status: "active",
    sortOrder: 10
  };
}

async function loadProducts() {
  loading.value = true;
  try {
    products.value = await listAdminPaymentProducts();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "套餐列表加载失败。");
  } finally {
    loading.value = false;
  }
}

function openCreate() {
  editorMode.value = "create";
  Object.assign(form, blankProduct(), { sortOrder: products.value.length * 10 + 10 });
}

function openEdit(product: AdminPaymentProduct) {
  editorMode.value = "edit";
  Object.assign(form, {
    ...product,
    productType: product.productType || (product.petSlots > 0 && product.credits <= 0 ? "pet_slot" : "credits")
  });
}

function closeEditor() {
  editorMode.value = null;
}

async function submitProduct() {
  if (!validateProductForm()) {
    return;
  }

  saving.value = true;
  try {
    const payload = normalizeProductPayload();
    const response = await saveAdminPaymentProduct(payload);
    message.success(response.message);
    closeEditor();
    await loadProducts();
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "套餐保存失败。");
  } finally {
    saving.value = false;
  }
}

async function submitDeleteProduct(product: AdminPaymentProduct) {
  deletingProductId.value = product.productId;
  try {
    await deleteAdminPaymentProduct(product.productId);
    products.value = products.value.filter((item) => item.productId !== product.productId);
    if (detailProduct.value?.productId === product.productId) {
      detailProduct.value = null;
    }
    if (form.productId === product.productId) {
      closeEditor();
    }
    message.success("套餐已下架，前台不会再展示。");
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "套餐删除失败。");
  } finally {
    deletingProductId.value = "";
  }
}

async function syncStripeProducts() {
  syncingStripe.value = true;
  try {
    const response = await syncAdminPaymentProductsToStripe();
    message.success(`${response.message} 成功 ${response.syncedProducts}/${response.totalProducts} 个。`);
  } catch (currentError) {
    message.error(currentError instanceof Error ? currentError.message : "同步 Stripe 产品目录失败。");
  } finally {
    syncingStripe.value = false;
  }
}

function isEditingProduct(product: AdminPaymentProduct) {
  return editorMode.value === "edit" && form.productId === product.productId;
}

function formatMoney(product: Pick<AdminPaymentProduct, "amountCny" | "currency">) {
  const currency = product.currency || "CNY";
  const symbol = currency === "AUD" ? "A$" : currency === "CNY" ? "¥" : `${currency} `;
  return `${symbol}${product.amountCny.toFixed(2)} ${currency}`;
}

function normalizeProductPayload(): ProductForm {
  return {
    ...form,
    productId: editorMode.value === "create" ? "" : form.productId,
    region: form.region || "CN",
    currency: form.currency || (form.region === "GLOBAL" ? "AUD" : "CNY"),
    credits: form.productType === "credits" ? form.credits : 0,
    petSlots: form.productType === "pet_slot" ? form.petSlots : 0
  };
}

function validateProductForm() {
  if (!form.title.trim()) {
    message.warning("请填写套餐名称。");
    return false;
  }

  if (!form.description.trim()) {
    message.warning("请填写套餐描述，套餐描述会同步到 Stripe description。");
    return false;
  }

  return true;
}

function productTypeLabel(product: Pick<AdminPaymentProduct, "productType" | "credits" | "petSlots">) {
  return productTypeValue(product) === "pet_slot" ? "宠物位套餐" : "额度套餐";
}

function productTypeValue(product: Pick<AdminPaymentProduct, "productType" | "credits" | "petSlots">) {
  return product.productType || (product.petSlots > 0 && product.credits <= 0 ? "pet_slot" : "credits");
}

function regionLabel(region: string) {
  return String(region || "").toUpperCase() === "GLOBAL" ? "国际版" : "国内版";
}

function regionTagColor(region: string) {
  return String(region || "").toUpperCase() === "GLOBAL" ? "geekblue" : "green";
}

function isCreditsPackage(product: Pick<AdminPaymentProduct, "productType" | "credits" | "petSlots">) {
  return productTypeValue(product) === "credits";
}

function isPetSlotPackage(product: Pick<AdminPaymentProduct, "productType" | "credits" | "petSlots">) {
  return productTypeValue(product) === "pet_slot";
}
</script>

<template>
  <a-space class="admin-page-stack" direction="vertical" size="large">
    <a-row :gutter="[16, 16]">
      <a-col :xs="24" :md="8"><a-card><a-statistic title="套餐总数" :value="products.length" /></a-card></a-col>
      <a-col :xs="24" :md="8"><a-card><a-statistic title="启用套餐" :value="activeCount" /></a-card></a-col>
      <a-col :xs="24" :md="8"><a-card><a-statistic title="国际版启用套餐" :value="globalActiveCount" /></a-card></a-col>
    </a-row>

    <a-card>
      <template #title>购买套餐</template>
      <template #extra>
        <a-space>
          <a-button :loading="syncingStripe" @click="syncStripeProducts">同步到 Stripe</a-button>
          <a-button type="primary" @click="openCreate">新增套餐</a-button>
        </a-space>
      </template>

      <a-table
        row-key="productId"
        :columns="columns"
        :data-source="products"
        :loading="loading"
        :pagination="{ pageSize: 10, showSizeChanger: true }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'title'">
            <strong>{{ record.title }}</strong>
            <div class="admin-table-subtext">{{ record.productId }}</div>
          </template>
          <template v-else-if="column.key === 'productType'">
            <a-tag :color="record.productType === 'pet_slot' ? 'purple' : 'blue'">{{ productTypeLabel(record) }}</a-tag>
          </template>
          <template v-else-if="column.key === 'region'">
            <a-tag :color="regionTagColor(record.region)">{{ regionLabel(record.region) }}</a-tag>
          </template>
          <template v-else-if="column.key === 'credits'">
            <span v-if="isCreditsPackage(record)">{{ record.credits }} 次</span>
            <span v-else class="admin-table-subtext">-</span>
          </template>
          <template v-else-if="column.key === 'petSlots'">
            <span v-if="isPetSlotPackage(record)">{{ record.petSlots }} 个</span>
            <span v-else class="admin-table-subtext">-</span>
          </template>
          <template v-else-if="column.key === 'amount'">{{ formatMoney(record) }}</template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="record.status === 'active' ? 'green' : 'default'">
              {{ record.status === "active" ? "启用" : "停用" }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'actions'">
            <a-space>
              <a-button size="small" @click="detailProduct = record">查看</a-button>
              <a-button size="small" type="primary" ghost @click="openEdit(record)">编辑</a-button>
              <a-popconfirm
                title="确定下架这个套餐吗？下架后前台不会再展示。"
                ok-text="下架"
                cancel-text="取消"
                @confirm="submitDeleteProduct(record)"
              >
                <a-button size="small" danger :disabled="isEditingProduct(record)" :loading="deletingProductId === record.productId">
                  下架
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      :title="editorMode === 'create' ? '新增套餐' : '编辑套餐'"
      :open="Boolean(editorMode)"
      :confirm-loading="saving"
      width="760px"
      ok-text="保存"
      cancel-text="取消"
      @ok="submitProduct"
      @cancel="closeEditor"
    >
      <a-form layout="vertical" :model="form">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="套餐类型">
              <a-select v-model:value="form.productType" :disabled="editorMode === 'edit'">
                <a-select-option value="credits">额度套餐</a-select-option>
                <a-select-option value="pet_slot">宠物位套餐</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="套餐名称" required><a-input v-model:value="form.title" /></a-form-item>
          </a-col>
        </a-row>
        <a-form-item v-if="editorMode === 'edit'" label="套餐 ID">
          <a-input v-model:value="form.productId" disabled />
        </a-form-item>
        <a-form-item label="套餐描述" required><a-textarea v-model:value="form.description" :rows="3" placeholder="会同步到 Stripe 的 description 字段" /></a-form-item>
        <a-row :gutter="16">
          <a-col :span="8">
            <a-form-item label="地区">
              <a-select v-model:value="form.region">
                <a-select-option value="CN">国内版</a-select-option>
                <a-select-option value="GLOBAL">国际版</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col v-if="isCreditsProduct" :span="8"><a-form-item label="生成额度"><a-input-number v-model:value="form.credits" :min="1" style="width: 100%" /></a-form-item></a-col>
          <a-col v-if="isPetSlotProduct" :span="8"><a-form-item label="宠物位"><a-input-number v-model:value="form.petSlots" :min="1" style="width: 100%" /></a-form-item></a-col>
          <a-col :span="8"><a-form-item label="价格"><a-input-number v-model:value="form.amountCny" :min="0" :step="0.01" style="width: 100%" /></a-form-item></a-col>
          <a-col :span="8">
            <a-form-item label="币种">
              <a-select v-model:value="form.currency">
                <a-select-option value="CNY">CNY 人民币</a-select-option>
                <a-select-option value="AUD">AUD 澳币</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="8"><a-form-item label="排序"><a-input-number v-model:value="form.sortOrder" style="width: 100%" /></a-form-item></a-col>
          <a-col :span="8">
            <a-form-item label="状态">
              <a-select v-model:value="form.status">
                <a-select-option value="active">启用</a-select-option>
                <a-select-option value="inactive">停用</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-modal>

    <a-drawer title="套餐详情" :open="Boolean(detailProduct)" width="460" @close="detailProduct = null">
      <a-descriptions v-if="detailProduct" bordered :column="1" size="small">
        <a-descriptions-item label="套餐 ID">{{ detailProduct.productId }}</a-descriptions-item>
        <a-descriptions-item label="类型">{{ productTypeLabel(detailProduct) }}</a-descriptions-item>
        <a-descriptions-item label="名称">{{ detailProduct.title }}</a-descriptions-item>
        <a-descriptions-item label="描述">{{ detailProduct.description || "暂无描述" }}</a-descriptions-item>
        <a-descriptions-item label="地区">{{ regionLabel(detailProduct.region) }}</a-descriptions-item>
        <a-descriptions-item v-if="isCreditsPackage(detailProduct)" label="额度">{{ detailProduct.credits }} 次</a-descriptions-item>
        <a-descriptions-item v-if="isPetSlotPackage(detailProduct)" label="宠物位">{{ detailProduct.petSlots }} 个</a-descriptions-item>
        <a-descriptions-item label="价格">{{ formatMoney(detailProduct) }}</a-descriptions-item>
        <a-descriptions-item label="状态">{{ detailProduct.status }}</a-descriptions-item>
        <a-descriptions-item label="排序">{{ detailProduct.sortOrder }}</a-descriptions-item>
      </a-descriptions>
      <template #footer>
        <a-space v-if="detailProduct">
          <a-button type="primary" @click="openEdit(detailProduct)">编辑套餐</a-button>
          <a-popconfirm
            title="确定下架这个套餐吗？下架后前台不会再展示。"
            ok-text="下架"
            cancel-text="取消"
            @confirm="submitDeleteProduct(detailProduct)"
          >
            <a-button danger :loading="deletingProductId === detailProduct.productId">下架套餐</a-button>
          </a-popconfirm>
        </a-space>
      </template>
    </a-drawer>
  </a-space>
</template>
